import { buildAlignment, drapePointsCounted, type Alignment, type ClipDims, type DrapeSampler } from '@/core/alignment';
import { atom } from 'jotai';

/**
 * Source-agnostic alignment registry. Producers (LandXML.tsx, GeoJson.tsx)
 * register the alignments they own under an `ownerKey` (their layer id);
 * consumers (walk toolbar, overlay, properties drawers) only read
 * `alignmentsAtom`.
 */
const registryAtom = atom<{ byKey: Map<string, Alignment>; byOwner: Map<string, string[]> }>({
  byKey: new Map(),
  byOwner: new Map(),
});

/**
 * `alignmentKey` → the layer id whose TIN its Z should be sampled from.
 *
 * Set from the station-volumes dialog when the chosen alignment turns out to
 * have no vertical profile (a LandXML `<CoordGeom>` with Z = 0 and no
 * `<Profile>`, which is how several exporters write a horizontal-only design).
 * Session-scoped: it is a way to make the corridor visible now, not a property
 * of the file, and it is deliberately not persisted.
 *
 * Applied by `alignmentsAtom` below rather than by the dialog, so the drape
 * reaches every consumer at once — the swath preview, the corridor bands, the
 * walk camera and clip box, the overlay line and ticks, the z/grade readout —
 * instead of each growing its own copy of the same fix.
 */
export const alignmentDrapeOverridesAtom = atom<Map<string, string>>(new Map());

/**
 * Keyed by the SOURCE alignment instance, so a producer re-registering an
 * identical alignment (a layer-settings tweak, a refetch) reuses the draped
 * result instead of re-sampling the whole polyline, and consumers memoised on
 * alignment identity do not see a new object every render.
 */
const drapeCache = new WeakMap<Alignment, { sampler: DrapeSampler; out: Alignment }>();

function drapeOnto(a: Alignment, sampler: DrapeSampler): Alignment {
  const hit = drapeCache.get(a);
  if (hit && hit.sampler === sampler) return hit.out;
  const { points, hits } = drapePointsCounted(a.points, sampler);
  // A surface that does not reach under this alignment is not a drape — same
  // rule as `landxml.ts`'s applyElevation. Returning `a` keeps `zSource` at
  // whatever it honestly was, so the dialog's "no vertical profile" note stays up.
  const out =
    hits === 0
      ? a
      : // buildAlignment re-adds START/END, so strip the synthetic ones first
        // (same dance as LandXML.tsx's `toWorld`).
        (buildAlignment({
          ...a,
          points,
          zSource: 'DRAPED',
          keyPoints: a.keyPoints.filter((k) => k.kind !== 'START' && k.kind !== 'END'),
        }) ?? a);
  drapeCache.set(a, { sampler, out });
  return out;
}

export const alignmentsAtom = atom((get) => {
  const raw = get(registryAtom).byKey;
  const overrides = get(alignmentDrapeOverridesAtom);
  if (overrides.size === 0) return raw;
  const sources = get(alignmentDrapeSourcesAtom);
  // Copy-on-write: with no override actually resolving to a sampler this
  // returns the registry map itself, so the common case allocates nothing and
  // keeps referential identity for consumers that depend on it.
  let out: Map<string, Alignment> | null = null;
  for (const [key, a] of raw) {
    const layerId = overrides.get(key);
    if (layerId === undefined) continue;
    const sampler = sources.get(layerId);
    if (!sampler) continue;
    const draped = drapeOnto(a, sampler);
    if (draped === a) continue;
    if (!out) out = new Map(raw);
    out.set(key, draped);
  }
  return out ?? raw;
});

/**
 * Layer id of an alignment that is not saved anywhere yet: the "Alignment from
 * rails" trace while it is in review (`lib/railTrace/useDraftAlignment.ts`),
 * registered so the walk tool can step along it. Not a real layer.
 */
export const DRAFT_ALIGNMENT_LAYER_ID = 'railTraceDraft';
export const isDraftAlignment = (a: Pick<Alignment, 'layerId'>) => a.layerId === DRAFT_ALIGNMENT_LAYER_ID;

const analysisCache = new WeakMap<ReadonlyMap<string, Alignment>, Map<string, Alignment>>();
/**
 * `alignmentsAtom` without drafts: what analysis pickers and defaults offer
 * (clearance, station volumes, compare, layer stack, crest guide, re-runs).
 * A draft is only there to be walked; a run on it would point at a feature
 * that does not exist. With no draft registered this is the registry map
 * itself, so consumers keep referential identity.
 */
export const analysisAlignmentsAtom = atom((get) => {
  const all = get(alignmentsAtom);
  let hasDraft = false;
  for (const a of all.values()) if (isDraftAlignment(a)) hasDraft = true;
  if (!hasDraft) return all;
  const hit = analysisCache.get(all);
  if (hit) return hit;
  const out = new Map([...all].filter(([, a]) => !isDraftAlignment(a)));
  analysisCache.set(all, out);
  return out;
});

export const registerAlignmentsAtom = atom(
  null,
  (get, set, { ownerKey, alignments }: { ownerKey: string; alignments: Alignment[] }) => {
    const prev = get(registryAtom);
    const byKey = new Map(prev.byKey);
    const byOwner = new Map(prev.byOwner);
    for (const k of byOwner.get(ownerKey) ?? []) byKey.delete(k);
    for (const a of alignments) byKey.set(a.key, a);
    byOwner.set(ownerKey, alignments.map((a) => a.key));
    set(registryAtom, { byKey, byOwner });
    const walk = get(alignmentWalkAtom);
    if (walk && !byKey.has(walk.key)) set(alignmentWalkAtom, null);
    const sel = get(selectedAlignmentKeyAtom);
    if (sel && !byKey.has(sel)) set(selectedAlignmentKeyAtom, null);
  },
);

export const unregisterAlignmentsAtom = atom(null, (_get, set, ownerKey: string) => {
  set(registerAlignmentsAtom, { ownerKey, alignments: [] });
});

/** Re-exported for consumers that only import from the atom module. */
export type { DrapeSampler } from '@/core/alignment';
/** `(x, y) → z` on a loaded TIN, keyed by the layer id that owns the TIN. */
export const alignmentDrapeSourcesAtom = atom<Map<string, DrapeSampler>>(new Map());

export const DEFAULT_WALK_STEP = 20;
/** Thin cross-section slice: 2 m along, 40 m across, 20 m tall. */
export const DEFAULT_WALK_CLIP: ClipDims = { length: 2, width: 40, height: 20 };

export interface AlignmentWalkState {
  key: string;
  /** Internal station. */
  station: number;
  /** Metres per ◀ / ▶ click. */
  step: number;
  snapKeyPoints: boolean;
  follow: boolean;
  /** Clip box follows the station as a cross-section slice; null/undefined = off. */
  clip?: ClipDims | null;
}
export const alignmentWalkAtom = atom<AlignmentWalkState | null>(null);

/**
 * The track readout under the pointer in 3D (`3D/AlignmentProbe.tsx`):
 * alignment key, internal station, the pointer's client position for the
 * card. `pinned` = clicked: stays until a click elsewhere or Esc.
 */
export interface AlignmentProbeState {
  key: string;
  station: number;
  clientX: number;
  clientY: number;
  pinned: boolean;
}
export const alignmentProbeAtom = atom<AlignmentProbeState | null>(null);

/** Highlighted (picked) alignment — may differ from the walked one. */
export const selectedAlignmentKeyAtom = atom<string | null>(null);
